import { useEffect, useSyncExternalStore } from "react";

/**
 * A page's OWN, deeper walkthrough — offered at the end of its page help.
 *
 * Page help explains a screen: here is the list, here is a customer's profile.
 * Some screens have more to teach than that. Orders has a real guided tour that
 * puts a sample order on the store and walks the seller through confirming,
 * preparing and shipping it — the workflow, not the furniture. It already
 * existed, offered once on a first-run store and never again.
 *
 * So the last page-help step hands over to it: "want to try it on a real
 * order?". The launcher lives on the shell and knows nothing about orders, and
 * the tour lives in the page and knows nothing about help — this is the seam
 * between them. A module-level slot is enough because exactly one page is
 * mounted at a time, and it is cleared on unmount.
 */
let runner: (() => void) | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Registers this page's deeper tour for as long as the page is mounted. */
export function useProvidePageTour(run: (() => void) | null): void {
  useEffect(() => {
    if (!run) return;
    runner = run;
    emit();
    return () => {
      // Only clear OUR runner: a page unmounting after the next one mounted
      // would otherwise wipe the new page's offer.
      if (runner === run) {
        runner = null;
        emit();
      }
    };
  }, [run]);
}

/** True while the page on screen has a deeper tour to offer. */
export function usePageTourAvailable(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => runner !== null,
    () => false,
  );
}

/** Starts it. No-op if the page has none, which the caller has checked. */
export function runPageTour(): void {
  runner?.();
}
